import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/hooks/useTenant';
import { dlog } from '@/utils/devlog';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

export const ProtectedRoute = ({ children, adminOnly }: ProtectedRouteProps) => {
  const {
    user,
    loading: authLoading,
    session,
    hasInitialLoad,
    isOnlyMember,
    clientIds,
    isAdmin,
    signOut,
    isAuthReady,
    profile,
  } = useAuth();
  const { isCustomDomain, loading: tenantLoading, tenant } = useTenant();
  const location = useLocation();

  const isLoading =
    authLoading ||
    tenantLoading ||
    !hasInitialLoad.current ||
    !isAuthReady ||
    (user && profile === null);

  const [permissionChecked, setPermissionChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [denied, setDenied] = useState(false);
  const signOutCalled = useRef(false);
  const checkLock = useRef(false);

  useEffect(() => {
    // dlog('[route:guard]', {
    //   user: user?.id,
    //   session: session?.user?.id,
    //   metadata: user?.user_metadata,
    //   authLoading,
    //   tenantLoading,
    //   isOnlyMember,
    //   clientIds,
    //   isAdmin,
    //   isCustomDomain,
    //   pathname: location.pathname,
    //   search: location.search,
    //   timestamp: new Date().toISOString(),
    // });
    // console.log('[ProtectedRoute] Auth state:', {
    //   user: user?.id,
    //   session: session?.user?.id,
    //   metadata: user?.user_metadata,
    //   authLoading,
    //   tenantLoading,
    //   isOnlyMember,
    //   clientIds,
    //   isAdmin,
    //   isCustomDomain,
    //   pathname: location.pathname,
    //   search: location.search,
    //   tenantId: tenant?.id || 'null',
    //   timestamp: new Date().toISOString(),
    // });
  }, [
    user,
    session,
    authLoading,
    tenantLoading,
    isOnlyMember,
    clientIds,
    isAdmin,
    isCustomDomain,
    location.pathname,
    location.search,
    tenant,
  ]);

  useEffect(() => {
    if (isLoading || permissionChecked || checkLock.current) return;

    checkLock.current = true;

    const checkPermission = async () => {
      try {
        if (
          isCustomDomain &&
          location.pathname.startsWith('/portal') &&
          user &&
          !denied
        ) {
          if (!tenant?.id || !tenant.user_id) {
            // console.log(
            //   '[ProtectedRoute] No tenant id or user_id on custom portal, denying access'
            // );
            setHasPermission(false);
          } else {
            // Check ownership
            const owns = String(tenant.user_id) === String(user.id);

            // Check direct membership to the tenant's client_id
            const { data: membershipData, error: membershipError } =
              await supabase
                .from('client_memberships')
                .select('client_id')
                .eq('client_id', tenant.id)
                .eq('user_id', user.id)
                .maybeSingle();

            let hasMembership = !membershipError && !!membershipData;

            if (!hasMembership && !owns) {
              // Revised check for members: Fetch the member's primary client and verify if its owner matches the tenant's owner
              const { data: memberMembership, error: memberError } =
                await supabase
                  .from('client_memberships')
                  .select('client_id')
                  .eq('user_id', user.id)
                  .eq('role', 'member') // Assume 'member' role for primary
                  .maybeSingle();

              if (!memberError && memberMembership) {
                const { data: clientData, error: clientError } = await supabase
                  .from('clients')
                  .select('user_id, settings')
                  .eq('id', memberMembership.client_id)
                  .single();

                hasMembership =
                  !clientError &&
                  String(clientData.user_id) === String(tenant.user_id) &&
                  clientData.settings?.inherits_business_branding;
              }
            }

            const belongs = owns || hasMembership;

            // console.log('[ProtectedRoute] Unified belonging check:', {
            //   owns,
            //   hasMembership,
            //   isAdmin, // Logged but not used
            //   belongs,
            //   isOnlyMember,
            //   tenantId: tenant.id,
            //   userId: user.id,
            //   timestamp: new Date().toISOString(),
            // });
            setHasPermission(belongs);
          }
        } else {
          // console.log('[ProtectedRoute] Skipping belonging check', {
          //   isCustomDomain,
          //   path: location.pathname,
          //   hasUser: !!user,
          //   denied,
          //   timestamp: new Date().toISOString(),
          // });
          setHasPermission(true);
        }
      } catch (err) {
        // console.error('[ProtectedRoute] Permission check error:', err);
        setHasPermission(false);
      } finally {
        setPermissionChecked(true);
        checkLock.current = false;
      }
    };

    checkPermission();
  }, [
    isLoading,
    permissionChecked,
    isCustomDomain,
    location.pathname,
    user,
    tenant,
    isOnlyMember,
    denied,
    supabase,
  ]);

  if (isLoading) {
    // console.log(
    //   '[ProtectedRoute] Still loading (auth or tenant or authReady or data), showing spinner',
    //   {
    //     authLoading,
    //     tenantLoading,
    //     hasInitialLoad: hasInitialLoad.current,
    //     isAuthReady,
    //     profileLoaded: profile !== null,
    //     hostname: window.location.hostname,
    //   }
    // );
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary'></div>
          <p className='mt-4 text-muted-foreground'>Verifying access...</p>
        </div>
      </div>
    );
  }

  // console.log('[ProtectedRoute] Loading complete, proceeding to checks', {
  //   isCustomDomain,
  //   hostname: window.location.hostname,
  //   fullUrl: window.location.href,
  //   tenantId: tenant?.id || 'null',
  // });

  if (isCustomDomain && !location.pathname.startsWith('/portal')) {
    // console.log(
    //   '[ProtectedRoute] Custom domain on non-portal route, redirecting',
    //   {
    //     pathname: location.pathname,
    //     user: user?.id,
    //     isOnlyMember,
    //   }
    // );
    if (!user || !session) {
      return <Navigate to='/portal/auth' replace />;
    } else if (isOnlyMember) {
      return <Navigate to='/portal/dashboard' replace />;
    } else {
      window.location.assign('https://app.callsparker.com/');
      return null;
    }
  }

  if (
    isCustomDomain &&
    location.pathname.startsWith('/portal') &&
    user &&
    permissionChecked &&
    !hasPermission &&
    !denied
  ) {
    // console.log(
    //   '[ProtectedRoute] No permission for this domain, signing out and redirecting to auth with error'
    // );
    if (!signOutCalled.current) {
      signOutCalled.current = true;
      setDenied(true);
      signOut();
    }
    return <Navigate to='/portal/auth?error=permission_denied' replace />;
  }

  if (denied) {
    return <Navigate to='/portal/auth?error=permission_denied' replace />;
  }

  if (
    location.pathname === '/login' ||
    location.pathname.startsWith('/portal/auth') ||
    location.pathname === '/portal/invite' ||
    location.pathname === '/portal/reset-password' || // New: Allow reset password
    location.pathname === '/portal/forgot-password' // New: Allow forgot password
  ) {
    // console.log('[ProtectedRoute] On login page, rendering content', {
    //   pathname: location.pathname,
    // });
    return <>{children}</>;
  }

  if (!user || !session) {
    // console.log('[ProtectedRoute] isCustomDomain value:', isCustomDomain);
    const redirectPath = isCustomDomain
      ? '/portal/auth'
      : location.pathname.startsWith('/portal')
      ? '/portal/auth'
      : '/login';
    // console.log(
    //   '[ProtectedRoute] No user or session, redirecting to:',
    //   redirectPath,
    //   {
    //     hostname: window.location.hostname,
    //     tenantLoading,
    //     fullUrl: window.location.href,
    //   }
    // );
    return <Navigate to={redirectPath} replace />;
  }

  if (
    user.user_metadata?.requires_password &&
    location.pathname !== '/set-password'
  ) {
    return <Navigate to='/set-password' replace />;
  }

  // console.log('[ProtectedRoute] Current role check:', {
  //   isOnlyMember,
  //   isAdmin,
  //   pathname: location.pathname,
  // });

  if (isOnlyMember && !isAdmin) {
    const allowedRoutes = [
      '/portal/dashboard',
      ...clientIds.map((clientId) => `/portal/client/${clientId}/actions`),
    ];
    const normalizedPathname = location.pathname.split('?')[0];
    if (!allowedRoutes.includes(normalizedPathname)) {
      // console.log(
      //   '[ProtectedRoute] Member-only user attempted to access unauthorized route:',
      //   location.pathname
      // );
      return <Navigate to='/portal/dashboard' replace />;
    }
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to='/dashboard' replace />;
  }

  // console.log(
  //   '[ProtectedRoute] User authenticated, rendering protected content'
  // );
  return <>{children}</>;
};
